// 1:1 port of prototype/components/script/PromptBox.jsx (design D3/D11): same
// name, same prop contract (prototype/components/script/PromptBox.d.ts), styled
// only from token CSS vars. UI.md §4.3/client-colony "Prompt box status cycle,
// advisory cooldown, and diagnostics": the inline status line cycles the
// BEEKEEPER §5 states (generating → repairing{attempt} → accepted{version} /
// failed{diagnostics}), with an advisory cooldown appended once accepted
// (design D7 — server-authoritative via `Rejected{rateLimited}`, this is a
// display hint only).
//
// `notReadyNotice` is caller-supplied (design D7, architecture-review F1): the
// caller (`ColonyController.dispatch` returning `not-ready`, task group 8) never
// updates `status` on a not-ready dispatch, so this presentational component
// never fabricates a pending/generating state on its own — it only ever reflects
// whatever `status`/`notReadyNotice` its caller passes down.

import type { CSSProperties } from 'react';
import { useState } from 'react';
import { Button } from '../core/Button';
import { Chip } from '../core/Chip';
import { ChatReentryRow } from '../chat/ChatReentryRow';

/** Mirrors the BEEKEEPER §5 pipeline states (`PromptStatusKind` on the wire),
 * minus `idle`'s own case being the default. */
export type PromptBoxStatus =
  | { kind: 'idle' }
  | { kind: 'generating' }
  | { kind: 'repairing'; attempt: number }
  | { kind: 'accepted'; version: number }
  | { kind: 'failed'; diagnostics?: string[] }
  | { kind: 'stale' };

/** The rounds-mode re-entry row's content (beekeeper-agent-chat, `client-beekeeper-chat`; sketch
 * `promptbox-entry.html`'s "Idle, with a thread to return to" state) — `ui/chat/transcript-model
 * .ts`'s `ThreadSummary`, threaded here rather than imported so this presentational component
 * stays free of a dependency on the chat domain model. */
export interface PromptBoxThreadSummary {
  lastLine: string;
  messageCount: number;
  busy: boolean;
}

export interface PromptBoxProps {
  placeholder?: string;
  status?: PromptBoxStatus;
  /** Advisory-only cooldown countdown shown once `status.kind === 'accepted'`
   * (design D7 — a display hint; `Rejected{rateLimited}` is authoritative). */
  cooldownSeconds?: number;
  /** A transient notice from a `dispatch()` that returned `not-ready` — Send
   * stays available, the box never enters a pending state that would strand
   * (architecture-review F1). */
  notReadyNotice?: string;
  /** Spectator/eliminated session: disables the input and Send, and replaces
   * the status line with this copy. */
  disabledReason?: string;
  /** Optional starter-prompt suggestions (onboarding design D-Starter). When present
   * — and the draft is empty, the box is neither disabled nor busy, and the caller's
   * `startersSuppressed` gate is clear — a `core/Chip` row renders. Tapping a chip
   * FILLS the draft with its `prompt` (never sends), so no Beekeeper cooldown is
   * charged by accident (charged only on acceptance, BEEKEEPER §5). */
  starterPrompts?: readonly { label: string; prompt: string }[];
  /** The caller-computed player-wide gate (onboarding design D-Starter,
   * architecture-review F1/F3): when true, the starter-prompt row is suppressed even
   * with an empty draft — a player-wide cooldown / rate-limit is in effect (or the box
   * is disabled/busy), so a send would bounce. The draft-empty condition is owned here
   * because `text` is this component's internal state; every cross-type signal the box
   * cannot see is folded into this one boolean by `Colony.tsx`. */
  startersSuppressed?: boolean;
  /** Optional heading rendered above the starter-prompt chips. */
  starterHint?: string;
  /** Optional footnote rendered below the box when the starter-prompt chips show
   * (the cooldown-on-accept note, design D-Content). */
  starterMicrocopy?: string;
  onSend?: (prompt: string) => void;
  /**
   * Rounds mode's re-entry control (`client-beekeeper-chat` "A separate control opens an existing
   * thread without sending"; `client-colony` "the prompt control opens the chat overlay"). Present
   * only when a thread exists to reopen — its mere presence is what renders the row, mirroring
   * every other "renders only when wired" seam in this kit. Tapping it calls `onReopenThread`
   * rather than `onSend`, so a conversation is re-readable without starting a new turn.
   */
  threadSummary?: PromptBoxThreadSummary;
  onReopenThread?: () => void;
  style?: CSSProperties;
}

type StatusLineClass = 'muted' | 'gen' | 'warn' | 'ok';
interface StatusLine {
  text: string;
  className: StatusLineClass;
}

const STATUS_COPY: Record<'idle' | 'failed' | 'stale', StatusLine> = {
  idle: { text: '', className: 'muted' },
  failed: { text: "Couldn't apply that — try rewording", className: 'warn' },
  stale: { text: 'Out of date — try again', className: 'muted' },
};

function repairingLine(attempt: number): StatusLine {
  return { text: `🔧 Fixing a compile error… (try ${attempt} of 3)`, className: 'gen' };
}

function acceptedLine(version: number, cooldownSeconds: number): StatusLine {
  return {
    text: cooldownSeconds > 0 ? `✓ v${version} is live · cooling down ${cooldownSeconds}s` : `✓ v${version} is live`,
    className: 'ok',
  };
}

const GENERATING_LINE: StatusLine = { text: '🌿 Beekeeper thinking…', className: 'gen' };

const CLASS_COLOR: Record<StatusLineClass, string> = {
  muted: 'var(--text-muted)',
  gen: 'var(--honey-600)',
  warn: 'var(--danger)',
  ok: 'var(--accent-strong)',
};

export function PromptBox({
  placeholder = '🐝 Tell the Beekeeper what to change…',
  status = { kind: 'idle' },
  cooldownSeconds = 0,
  notReadyNotice,
  disabledReason,
  starterPrompts,
  startersSuppressed = false,
  starterHint,
  starterMicrocopy,
  onSend,
  threadSummary,
  onReopenThread,
  style,
}: PromptBoxProps) {
  const [text, setText] = useState('');
  const disabled = Boolean(disabledReason);
  const busy =
    status.kind === 'generating' ||
    status.kind === 'repairing' ||
    (status.kind === 'accepted' && cooldownSeconds > 0);
  const inputDisabled = disabled || busy;

  // The starter-prompt row shows ONLY when the draft is empty AND a send would
  // actually be accepted (onboarding design D-Starter): the empty-draft check is owned
  // here (`text` is internal); the disabled/busy/player-wide-cooldown/rate-limit checks
  // arrive folded into `startersSuppressed`, with the local `inputDisabled` as a
  // belt-and-suspenders guard so the box never invites a fill on a disabled/busy box.
  const showStarters =
    starterPrompts !== undefined &&
    starterPrompts.length > 0 &&
    !startersSuppressed &&
    !inputDisabled &&
    text.trim() === '';

  const line: StatusLine = disabled
    ? { text: disabledReason ?? '', className: 'muted' }
    : status.kind === 'repairing'
      ? repairingLine(status.attempt)
      : status.kind === 'accepted'
        ? acceptedLine(status.version, cooldownSeconds)
        : status.kind === 'generating'
          ? GENERATING_LINE
          : (STATUS_COPY[status.kind] ?? STATUS_COPY.idle);

  return (
    <div
      style={{
        background: 'var(--surface-card)',
        border: '1.5px solid var(--line-strong)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        opacity: disabled ? 0.55 : 1,
        ...style,
      }}
    >
      {threadSummary && (
        <ChatReentryRow
          lastLine={threadSummary.lastLine}
          busy={threadSummary.busy}
          onOpen={() => onReopenThread?.()}
        />
      )}
      {notReadyNotice && !disabled && (
        <div
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'var(--danger)',
            background: 'var(--danger-soft)',
            borderRadius: 'var(--r-md)',
            padding: '7px 10px',
          }}
        >
          {notReadyNotice}
        </div>
      )}
      {showStarters && (
        <div
          data-testid="starter-prompt-chips"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}
        >
          {starterHint && (
            <div
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-caps)',
                color: 'var(--accent-strong)',
              }}
            >
              {starterHint}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {starterPrompts!.map((sp) => (
              <Chip
                key={sp.label}
                icon={<span aria-hidden style={{ color: 'var(--accent-strong)' }}>✎</span>}
                onClick={() => setText(sp.prompt)}
              >
                {sp.label}
              </Chip>
            ))}
          </div>
        </div>
      )}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={disabled ? 'Spectating — prompts are disabled' : placeholder}
        disabled={inputDisabled}
        rows={2}
        // Borderless, inside the box's own padded frame — so the shell's standalone-control
        // padding and min-height would double the inset here (`ui/app-shell.css`).
        data-ui-field-control=""
        style={{
          border: 'none',
          outline: 'none',
          resize: 'none',
          background: 'transparent',
          fontFamily: 'var(--font-body)',
          fontSize: 15,
          lineHeight: 1.5,
          color: 'var(--ink-1)',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: CLASS_COLOR[line.className], flex: 1, minWidth: 0 }}>
          {line.text}
        </span>
        <Button
          variant="primary"
          disabled={inputDisabled || !text.trim()}
          onClick={() => {
            onSend?.(text);
            setText('');
          }}
        >
          Send
        </Button>
      </div>
      {showStarters && starterMicrocopy && (
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-muted)' }}>
          {starterMicrocopy}
        </div>
      )}
      {status.kind === 'failed' && status.diagnostics && status.diagnostics.length > 0 && (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--danger)',
            background: 'var(--danger-soft)',
            borderRadius: 'var(--r-md)',
            padding: '8px 10px',
          }}
        >
          {status.diagnostics.join('; ')}
        </div>
      )}
    </div>
  );
}
